import {
  DefaultMetricOptions,
  Goal,
  GoalCompletion,
  GoalProgressCount,
  GoalTreeIndex,
  ID,
} from '@griply/types';

import { calculateMetricEntryValue } from './calculate-metric-entry-value';
import { getSubGoals } from './goals/get-sub-goals';

export type GetGoalCompletionOptions = {
  goals: Goal[];
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  index?: GoalTreeIndex;
};

export const getGoalCompletion = (
  goal: Goal,
  { goals, goalProgressCounts, index }: GetGoalCompletionOptions,
): GoalCompletion => {
  // The index is keyed on the goals array alone, but live task counts change
  // independently of it — so a completed-tasks metric must never read or seed
  // the per-goal completion cache, or it would serve a stale count.
  const isCacheable = goal.metric?.id !== DefaultMetricOptions.CompletedTasks;

  const cached = isCacheable
    ? index?.completionByGoalId.get(goal.id)
    : undefined;
  if (cached) {
    return cached;
  }

  const completion = computeGoalCompletion(
    goal,
    goals,
    goalProgressCounts,
    index,
  );
  if (isCacheable) {
    index?.completionByGoalId.set(goal.id, completion);
  }

  return completion;
};

const computeGoalCompletion = (
  goal: Goal,
  goals: Goal[],
  goalProgressCounts?: Map<ID, GoalProgressCount>,
  index?: GoalTreeIndex,
): GoalCompletion => {
  if (!goal.metric?.id) {
    return {
      start: 0,
      current: 0,
      end: Infinity,
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.CompletedTasks) {
    // no live counts yet — report an unmeasurable target rather than a false 0%
    if (!goalProgressCounts) {
      return {
        start: 0,
        current: 0,
        end: Infinity,
      };
    }

    const { completed, total } = goalProgressCounts.get(goal.id) ?? {
      completed: 0,
      total: 0,
    };

    return {
      start: 0,
      current: completed,
      end: total,
    };
  }

  if (goal.metric?.id === DefaultMetricOptions.SubGoalProgress) {
    const subGoals = getSubGoals(goal.id, { goals, recursive: true, index });

    return {
      start: 0,
      current: subGoals.filter(({ completedAt }) => !!completedAt).length,
      end: subGoals.length,
    };
  }

  const start = goal.metric?.startValue ?? 0;
  const lastEntry = goal.metric.entries
    ?.slice()
    .sort((entryA, entryB) => entryB.date.getTime() - entryA.date.getTime())[0];
  const current = lastEntry
    ? calculateMetricEntryValue(lastEntry.id, goal.metric.entries!, start)
    : start;

  return {
    start,
    end: goal.metric?.targetValue ?? Infinity,
    current,
  };
};
